import React, {useEffect, useState} from 'react';
import {TabItem, Tabs} from '../../../../shared';
import ProjectItems from '../../ProjectItems';
import ButtonMUI from '../../../../shared/ButtonMUI';
import {Controller, useForm} from 'react-hook-form';
import SelectComponent from '../../../../shared/SelectComponent';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {ReactComponent as ToReleaseIcon} from '../../../../assets/icons/fire-user.svg';
import {ReactComponent as WorkIcon} from '../../../../assets/icons/tabWork.svg';
import {ReactComponent as CompletedIcon} from '../../../../assets/icons/tabCompleted.svg';
import {ReactComponent as StoppedIcon} from '../../../../assets/icons/tabStoped.svg';
import {ReactComponent as PhoneIcon} from '../../../../assets/icons/phone.svg';
import {ReactComponent as EmailIcon} from '../../../../assets/icons/email.svg';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {notifySuccess} from '../../../../helpers/notifySnack';
import {
    getCompaniesClients,
    getCompaniesClientsList,
    postCompaniesClients,
    patchCompanyClientBlock,
    patchCompanyClientReturn
} from '../../../../redux/actions/clients/clientsActions';
import {clearUserRoles, getUserRoles} from '../../../../redux/actions/profile/profileActions';
import LoadPanel from '../../LoadPanel';
import {ReactComponent as UseAddIcon} from '../../../../assets/icons/user-add.svg';
import AvatarUser from '../../../../assets/images/avatars/avatar-users.png';
import './CustomerInner.scss';
import {
    getProject,
    getProjectMembersList,
    projectMemberRemove
} from '../../../../redux/actions/projects/projectsActions';
import {ReactComponent as DeleteIcon} from '../../../../assets/icons/delete-perticipants.svg';
import {showConfirmPopup} from '../../../../redux/actions/app/appActions';

const CustomerInner = ({
                           companyId,
                           setBtnLoading,
                           btnLoading,
                           projectId,
                           project,
                           handleList,
                           editPermission = false,
                           companiesClients,
                           viewProjectPermission = false,
                           blockCustomerPermission = false,
                           returnCustomerPermission = false,
                           removePermission= false
                       }) => {
    const dispatch = useDispatch();
    const clientsId = companiesClients?.id;
    const clientId = companiesClients?.clientId;
    const [selectedRole, setSelectedRole] = useState('');
    const [buttonLoad, setButtonLoad] = useState(false);
    const [buttonRemoveLoad, setButtonRemoveLoad] = useState(false);
    const {t} = useTranslation();

    const {userRoles} = useSelector((state) => state.profile);
    const profileMe = useSelector((state) => state.profile.profileMe);
    const isMe = profileMe?.id === clientsId?.id;

    const schema = yup.object({
        position: yup.string().required(t('validations.required'))
    });


    const {control, handleSubmit, reset} = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            position: ''
        }
    });

    const handleRemoveClient = async (id) => {
        setButtonRemoveLoad(true);
        try {
            const response = await dispatch(projectMemberRemove(projectId, id));
            if (response.payload?.status === 204) {
                await Promise.all([dispatch(getProjectMembersList(projectId)), dispatch(getProject(projectId))]);
                handleList();
                notifySuccess(t('all.successfully'));
            }
        } finally {
            setButtonRemoveLoad(false);
        }
    };

    const handleBlockInner = async () => {
        setBtnLoading(true);

        try {
            const response = await dispatch(patchCompanyClientBlock(companyId, clientsId));
            if (response.payload?.status === 200) {
                if (project) {
                    await Promise.all([dispatch(getProjectMembersList(projectId)), dispatch(getCompaniesClients(clientId))]);
                    notifySuccess(t('all.successfully'));
                } else {
                    await Promise.all([dispatch(getCompaniesClientsList(companyId)), dispatch(getCompaniesClients(clientId))]);
                    notifySuccess(t('all.successfully'));
                }
            }
        } finally {
            setBtnLoading(false);
        }
    };

    const handleReturnInner = async () => {
        setBtnLoading(true);
        try {
            const response = await dispatch(patchCompanyClientReturn(companyId, clientsId));
            if (response.payload?.status === 200) {
                if (project) {
                    await Promise.all([dispatch(getProjectMembersList(projectId)), dispatch(getCompaniesClients(clientId))]);
                    notifySuccess(t('all.successfully'));
                } else {
                    await Promise.all([dispatch(getCompaniesClientsList(companyId)), dispatch(getCompaniesClients(clientId))]);
                    notifySuccess(t('all.successfully'));
                }
            }
        } finally {
            setBtnLoading(false);
        }
    };

    const handleRemoveClientRedux = async (id) => {
        dispatch(
            showConfirmPopup({
                title: t('participant.removeTitle'),
                text: t('participant.removeText'),
                visible: true,
                confirmCallback: () => handleRemoveClient(id)
            })
        );
    };

    const handleBlockInnerRedux = async () => {
        dispatch(
            showConfirmPopup({
                title: t('participant.blockTitle'),
                text: t('participant.blockText'),
                visible: true,
                confirmCallback: () => handleBlockInner()
            })
        );
    };

    const handleReturnInnerRedux = async () => {
        dispatch(
            showConfirmPopup({
                title: t('participant.unblockTitle'),
                text: t('participant.unblockText'),
                visible: true,
                confirmCallback: () => handleReturnInner()
            })
        );
    };

    const activeProjects = companiesClients?.projects?.filter((project) => project.status === 'active');
    const completedProjects = companiesClients?.projects?.filter((project) => project.status === 'done');
    const pausedProjects = companiesClients?.projects?.filter((project) => project.status === 'pause');

    const position = userRoles.map((role) => ({
        id: role.id,
        role: role.role,
        name: role.name
    }));

    useEffect(() => {
        if (!userRoles.length) {
            dispatch(getUserRoles('client'));
        }
        return () => {
            dispatch(clearUserRoles());
        };
    }, [dispatch]);

    useEffect(() => {
        const defaultRole = userRoles.find((role) => role.role === companiesClients.role);
        if (defaultRole) {
            setSelectedRole(defaultRole.id);
            reset({position: defaultRole.id});
        }
    }, [companiesClients, userRoles, reset]);

    const onSubmit = async (data) => {
        setButtonLoad(true);
        try {
            const selectedRole = position.find((role) => role.id === data.position);

            if (!selectedRole) {
                throw new Error('Role not found');
            }

            const formData = new FormData();
            formData.append('position', selectedRole.role);

            const res = await dispatch(postCompaniesClients(companyId, clientsId, formData));

            if (res.payload?.status === 200) {
                if (project) {
                    await Promise.all([dispatch(getProjectMembersList(projectId)), dispatch(getCompaniesClients(clientId))]);
                } else {
                    await Promise.all([dispatch(getCompaniesClients(clientId)), dispatch(getCompaniesClientsList(companyId))]);
                }
                notifySuccess(t('all.successfully'));
            }
        } finally {
            setButtonLoad(false);
        }
    };

    if ((!companiesClients && !Object.keys(companiesClients)) || !userRoles.length) {
        return <LoadPanel noBg={true} circleSize={50} staticPos={true}/>;
    }

    return (
        <div className='customer-inner'>
            <div className='customer-inner__details'>
                <div className='customer-inner__details-info'>
                    <div className='name'>
                        <div className='avatar'>
                            {companiesClients.avatar ? (
                                <img src={companiesClients.avatar} alt={companiesClients.firstName}/>
                            ) : (
                                <img src={AvatarUser} alt={companiesClients.firstName}/>
                            )}
                            {companiesClients.isActive === true ? <div className='status active'/> :
                                <div className='status'/>}
                        </div>
                        {companiesClients.firstName}
                        <span>{companiesClients.positionName}</span>
                    </div>

                    <div className='customer-inner__details-customer'>
                        <div className='customer-inner__details-contact'>
                            <PhoneIcon/>
                            {companiesClients.phone ? companiesClients.phone : '-'}
                        </div>
                        <div className='customer-inner__details-contact'>
                            <EmailIcon/>
                            {companiesClients.email ? companiesClients.email : '-'}
                        </div>
                    </div>
                </div>
            </div>

            <form className='customer-inner__actions' onSubmit={handleSubmit(onSubmit)}>
                {editPermission && (
                    <div className='customer-inner__actions-select'>
                        <Controller
                            name='position'
                            control={control}
                            defaultValue={selectedRole}
                            render={({field}) => (
                                <SelectComponent
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={position.find((c) => c.name === field.value) || null}
                                    options={position}
                                    label={t('CustomerInner.label')}
                                    placeholder=''
                                    inputProps={field}
                                    disabled={!editPermission}
                                />
                            )}
                        />

                        <ButtonMUI loading={buttonLoad} formAction disabled={!editPermission || buttonLoad}>
                            {t('CustomerInner.change')}
                        </ButtonMUI>
                    </div>
                )}

                {project && removePermission && !isMe && (
                    <ButtonMUI
                        color='primary'
                        startIcon={<DeleteIcon/>}
                        onClick={() => handleRemoveClientRedux(companiesClients.id)}
                        loading={buttonRemoveLoad}
                    >
                        {t('CustomerInner.delete')}
                    </ButtonMUI>
                )}

                {companiesClients.isActive && blockCustomerPermission && !isMe ? (
                    <ButtonMUI onClick={handleBlockInnerRedux} loading={btnLoading}>
                        <ToReleaseIcon/>
                        {t('CustomerInner.block')}
                    </ButtonMUI>
                ) : (
                    <>
                        {returnCustomerPermission && !isMe && (
                            <ButtonMUI onClick={handleReturnInnerRedux} loading={btnLoading}>
                                <UseAddIcon/>
                                {t('CustomerInner.unblock')}
                            </ButtonMUI>
                        )}
                    </>
                )}
            </form>
            {viewProjectPermission && (
                <div className='customer-inner__tabs'>
                    <Tabs defaultIndex='1'>
                        <TabItem label={t('CustomerInner.in_progress')} icon={<WorkIcon/>} count={activeProjects.length}
                                 index='1'>
                            {activeProjects.length >= 1 ? (
                                <ProjectItems projects={activeProjects}/>
                            ) : (
                                <div className='project-no-items'>{t('CustomerInner.no_projects')}</div>
                            )}
                        </TabItem>
                        <TabItem
                            label={t('CustomerInner.completed')}
                            icon={<CompletedIcon/>}
                            count={completedProjects.length}
                            index='2'
                        >
                            {completedProjects.length >= 1 ? (
                                <ProjectItems projects={completedProjects}/>
                            ) : (
                                <div className='project-no-items'>{t('CustomerInner.no_projects')}</div>
                            )}
                        </TabItem>
                        <TabItem label={t('CustomerInner.paused')} icon={<StoppedIcon/>} count={pausedProjects.length}
                                 index='3'>
                            {pausedProjects.length >= 1 ? (
                                <ProjectItems projects={pausedProjects}/>
                            ) : (
                                <div className='project-no-items'>{t('CustomerInner.no_projects')}</div>
                            )}
                        </TabItem>
                    </Tabs>
                </div>
            )}
        </div>
    );
};

export default CustomerInner;
